import * as THREE from 'three/webgpu';
import { installThreeGcPatches } from './three-gc';

export type BackendKind = 'webgpu' | 'webgl2';

export interface RendererInfo {
  renderer: THREE.WebGPURenderer;
  backend: BackendKind;
  gpuName: string;
  compatibilityMode: boolean;
  timestamps: boolean;
}

const FORCE_KEY = 'loose-surface.forceWebGL';
/** Reloads after a lost device this session (a lost context can be a transient GPU reset; cap the loop). */
const LOST_KEY = 'loose-surface.deviceLost';
const MAX_RELOADS = 2;

function sessionGet(k: string): string | null {
  try {
    return sessionStorage.getItem(k);
  } catch {
    return null;
  }
}
function sessionSet(k: string, v: string): void {
  try {
    sessionStorage.setItem(k, v);
  } catch {
    /* storage unavailable */
  }
}

export function webglForced(): boolean {
  const q = new URLSearchParams(location.search);
  return q.has('webgl') || sessionGet(FORCE_KEY) === '1';
}

/** True if neither WebGPU nor WebGL2 can be used here. */
export function graphicsUnavailable(): boolean {
  const hasWebGPU = typeof navigator !== 'undefined' && 'gpu' in navigator;
  let hasWebGL2: boolean;
  try {
    hasWebGL2 = !!document.createElement('canvas').getContext('webgl2');
  } catch {
    hasWebGL2 = false;
  }
  return !hasWebGPU && !hasWebGL2;
}

/**
 * Creates the WebGPURenderer (automatic WebGL2 fallback if WebGPU setup fails). Device loss and
 * uncaught GPU errors don't trigger three's fallback, so we remember the failure for the session
 * and reload on the WebGL2 path.
 */
/** `gpuTimestamps`: GPU timestamp queries (benchmark and debug only; they cost per-pass work and GC churn). */
export async function createRenderer(canvas: HTMLCanvasElement, onFatal: (msg: string) => void, gpuTimestamps = false): Promise<RendererInfo> {
  const forceWebGL = webglForced();
  const renderer = new THREE.WebGPURenderer({
    canvas,
    antialias: false,
    forceWebGL,
    trackTimestamp: gpuTimestamps,
    powerPreference: 'high-performance',
  });
  await renderer.init();
  if (!installThreeGcPatches(renderer)) console.warn('[render] three GC patches not installed (three version changed)');
  const backendAny = renderer.backend as unknown as {
    isWebGPUBackend?: boolean;
    device?: { adapterInfo?: { vendor?: string; architecture?: string; description?: string; device?: string } };
    gl?: WebGL2RenderingContext;
    compatibilityMode?: boolean;
  };
  const backend: BackendKind = backendAny.isWebGPUBackend ? 'webgpu' : 'webgl2';
  let gpuName = 'unknown';
  if (backend === 'webgpu') {
    const ai = backendAny.device?.adapterInfo;
    gpuName = [ai?.description, ai?.vendor, ai?.architecture, ai?.device].filter((s) => s && s.length > 0).join(' / ') || 'WebGPU adapter';
    // WebGPU only exposes vendor/architecture here; the GPU's own name comes from WebGL's unmasked
    // renderer string (benchmark records, and the preset auto-detection).
    try {
      const c = document.createElement('canvas');
      const gl = c.getContext('webgl2');
      const ext = gl?.getExtension('WEBGL_debug_renderer_info');
      const name = gl && ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : '';
      // "ANGLE (AMD, AMD Radeon 780M Graphics (0x…) Direct3D11 …)" → "AMD Radeon 780M Graphics".
      const m = /ANGLE \([^,]*, ([^(,)]+)/.exec(name);
      const short = (m ? m[1] : name).trim();
      if (short) gpuName = `${short} (${gpuName})`;
      gl?.getExtension('WEBGL_lose_context')?.loseContext();
    } catch {
      /* keep the adapter description */
    }
  } else if (backendAny.gl) {
    const gl = backendAny.gl;
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    gpuName = ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : String(gl.getParameter(gl.RENDERER));
  }

  let handled = false;
  const fallback = (reason: string) => {
    if (handled) return;
    handled = true;
    const lost = Number(sessionGet(LOST_KEY) ?? 0);
    if (lost >= MAX_RELOADS) {
      onFatal(`The graphics device was lost again (${reason}). Reload the page to try once more.`);
      return;
    }
    sessionSet(LOST_KEY, String(lost + 1));
    // WebGPU: three's automatic WebGL2 fallback only covers start-up, so reload on WebGL2 for the
    // rest of the session. WebGL2: a lost context is often a transient GPU reset; reload.
    console.warn(`[render] GPU failure (${reason}); reloading${backend === 'webgpu' ? ' on WebGL2 for this session' : ''}`);
    if (backend === 'webgpu') sessionSet(FORCE_KEY, '1');
    location.reload();
  };
  renderer.onDeviceLost = (info) => fallback(`device lost: ${info.message}`);
  (renderer as unknown as { onError: (e: { message?: string; type?: string }) => void }).onError = (e) => {
    // Validation errors are bugs we log; out-of-memory/internal errors mean the device is unusable.
    const type = e?.type ?? '';
    console.error('[render] uncaptured GPU error', type, e?.message);
    if (type.includes('OutOfMemory') || type.includes('Internal')) fallback(`${type}: ${e?.message}`);
  };

  return {
    renderer,
    backend,
    gpuName,
    compatibilityMode: !!backendAny.compatibilityMode,
    timestamps: gpuTimestamps && renderer.hasFeature('timestamp-query'),
  };
}
